import React, { useMemo } from "react";
import {
  BankOutlined,
  BarChartOutlined,
  CalendarOutlined,
  DollarOutlined,
  FileTextOutlined,
  RiseOutlined,
  SyncOutlined,
  TeamOutlined,
  UserOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { Button } from "antd";
import DashboardHero from "./DashboardHero";
import DashboardSection from "./DashboardSection";
import DashboardMetric from "./DashboardMetric";
import EncountersBreakdown from "./EncountersBreakdown";
import { hasMetricValue } from "../utils";

const BRAND = {
  blue: "#3497D3",
  green: "#49B870",
  orange: "#faad14",
  red: "#ff4d4f",
  purple: "#722ed1",
};

function MetricGrid({ metrics, loading = false, skeletonCount = 4 }) {
  if (loading) {
    return (
      <>
        {Array.from({ length: skeletonCount }).map((_, i) => (
          <DashboardMetric key={`skel-${i}`} title="" value={0} loading variant="tile" />
        ))}
      </>
    );
  }

  const visible = metrics.filter((m) => m.show !== false && hasMetricValue(m.value));
  if (!visible.length) return null;
  return (
    <>
      {visible.map(({ key, ...metricProps }) => (
        <DashboardMetric key={key} {...metricProps} />
      ))}
    </>
  );
}

function buildHeroMetrics({ summary, counters, userRoleId, isClaimsStaff, isPAStaff, actions }) {
  const candidates = [];

  if ([1, 5].includes(userRoleId) && hasMetricValue(summary?.totalPolicyCost)) {
    candidates.push({
      key: "hero-revenue",
      title: "Total revenue",
      value: summary.totalPolicyCost,
      prefix: "₦",
      compact: true,
      color: BRAND.green,
      subtitle: "Policy revenue",
      onClick: actions.showOrgDrawer,
    });
  }

  if (hasMetricValue(summary?.totalEncounters)) {
    candidates.push({
      key: "hero-encounters",
      title: "Encounters",
      value: summary.totalEncounters,
      color: BRAND.blue,
      subtitle: "All time in view",
      onClick: () => actions.showCheckedClients(null),
    });
  }

  if (isClaimsStaff && hasMetricValue(summary?.totalClaimsPendingCost)) {
    candidates.push({
      key: "hero-claims-pending",
      title: "Pending payment",
      value: summary.totalClaimsPendingCost,
      prefix: "₦",
      compact: true,
      color: BRAND.orange,
      subtitle: "Claims awaiting payment",
      onClick: () => actions.navigate("/claims"),
    });
  }

  if (isPAStaff && hasMetricValue(counters?.pa?.pending)) {
    candidates.push({
      key: "hero-pa-pending",
      title: "Pending PA",
      value: counters.pa.pending,
      color: BRAND.orange,
      subtitle: "Awaiting review",
      onClick: () => actions.navigate("/preauth"),
    });
  }

  if (isClaimsStaff && hasMetricValue(counters?.claims?.pending_review)) {
    candidates.push({
      key: "hero-claims-review",
      title: "Needs review",
      value: counters.claims.pending_review,
      color: BRAND.red,
      subtitle: "Claims queue",
      onClick: () => actions.navigate("/claims"),
    });
  }

  if (hasMetricValue(summary?.activeEncounters)) {
    candidates.push({
      key: "hero-active",
      title: "Active now",
      value: summary.activeEncounters,
      color: BRAND.green,
      subtitle: "Live encounters",
      onClick: () => actions.showCheckedClients("active"),
    });
  }

  if (hasMetricValue(summary?.enrolees)) {
    candidates.push({
      key: "hero-enrollees",
      title: "Enrollees",
      value: summary.enrolees,
      color: BRAND.green,
      subtitle: "Registered members",
    });
  }

  const seen = new Set();
  return candidates.filter((item) => {
    if (seen.has(item.key)) return false;
    seen.add(item.key);
    return true;
  }).slice(0, 4);
}

const DashboardBody = ({
  loading,
  filters, summary,
  counters,
  userRoleId,
  isAdmin,
  isClaimsStaff,
  isPAStaff,
  collapsedSections,
  toggleSection,
  actions,
}) => {
  const heroMetrics = useMemo(
    () => buildHeroMetrics({ summary, counters, userRoleId, isClaimsStaff, isPAStaff, actions }),
    [summary, counters, userRoleId, isClaimsStaff, isPAStaff, actions]
  );

  const isCfo = [1, 5].includes(userRoleId);
  const goClaims = () => actions.navigate("/claims");
  const goPreauth = () => actions.navigate("/preauth");

  return (
    <div className="dashboard-content">
      <DashboardHero metrics={heroMetrics} loading={loading} />

      {isCfo ? (
        <DashboardSection
          id="revenue"
          icon={<DollarOutlined />}
          title="Revenue"
          subtitle="Financial performance"
          collapsed={collapsedSections.revenue}
          onToggle={toggleSection}
        >
          <MetricGrid
            loading={loading}
            metrics={[
              {
                key: "revenue-total",
                title: "Total revenue",
                value: summary?.totalPolicyCost,
                prefix: "₦",
                icon: <DollarOutlined />,
                color: BRAND.green,
                onClick: actions.showOrgDrawer,
                subtitle: "Total policy revenue",
              },
              {
                key: "revenue-wakaala",
                title: "Wakaala fee",
                value: parseFloat(summary?.wakaalaFee),
                prefix: "₦",
                icon: <RiseOutlined />,
                color: BRAND.blue,
                subtitle: "25% of total revenue",
              },
              {
                key: "revenue-nhis",
                title: "NHIS fee",
                value: parseFloat(summary?.nhisFee),
                prefix: "₦",
                icon: <FileTextOutlined />,
                color: BRAND.purple,
                subtitle: "1% of total revenue",
              },
              {
                key: "revenue-mlr",
                title: "Medical loss ratio",
                value: parseFloat(summary?.medical_loss_ratio),
                suffix: "%",
                icon: <BarChartOutlined />,
                color: BRAND.orange,
                subtitle: "MLR percentage",
              },
            ]}
          />
        </DashboardSection>
      ) : null}

      {isClaimsStaff ? (
        <DashboardSection
          id="claims"
          icon={<FileTextOutlined />}
          title="Claims & expenditure"
          subtitle="Processing and payments"
          collapsed={collapsedSections.claims}
          onToggle={toggleSection}
          action={
            <Button type="link" size="small" onClick={goClaims}>
              View all
            </Button>
          }
        >
          <MetricGrid
            loading={loading}
            metrics={[
              {
                key: "claims-total",
                title: "Total claims",
                value: summary?.totalClaimsCost,
                prefix: "₦",
                icon: <FileTextOutlined />,
                color: BRAND.red,
                onClick: goClaims,
                subtitle: "Total claims value",
              },
              {
                key: "claims-paid-cost",
                title: "Claims paid",
                value: summary?.totalClaimsPaidCost,
                prefix: "₦",
                icon: <DollarOutlined />,
                color: BRAND.green,
                onClick: goClaims,
                subtitle: "Total paid",
              },
              {
                key: "claims-pending-cost",
                title: "Pending payment",
                value: summary?.totalClaimsPendingCost,
                prefix: "₦",
                icon: <CalendarOutlined />,
                color: BRAND.orange,
                onClick: goClaims,
                subtitle: "Awaiting payment",
              },
              {
                key: "claims-filed",
                title: "Claims filed",
                value: summary?.totalClaimsFiled,
                icon: <FileTextOutlined />,
                color: BRAND.blue,
                onClick: goClaims,
                subtitle: "Total filed",
              },
              {
                key: "claims-paid-count",
                title: "Paid claims",
                value: summary?.totalPaidClaims,
                icon: <DollarOutlined />,
                color: BRAND.green,
                onClick: goClaims,
                subtitle: "Successfully paid",
              },
              {
                key: "claims-pending-count",
                title: "Pending claims",
                value: summary?.totalPendingClaims,
                icon: <CalendarOutlined />,
                color: BRAND.orange,
                onClick: goClaims,
                subtitle: "Under review",
              },
              {
                key: "claims-review",
                title: "Pending review",
                value: counters?.claims?.pending_review,
                icon: <WarningOutlined />,
                color: BRAND.red,
                onClick: goClaims,
                subtitle: "Requires attention",
              },
              {
                key: "claims-declined",
                title: "Declined",
                value: summary?.totalDeclinedClaims,
                icon: <FileTextOutlined />,
                color: BRAND.red,
                onClick: goClaims,
                subtitle: "Rejected claims",
              },
            ]}
          />
        </DashboardSection>
      ) : null}

      {isPAStaff ? (
        <DashboardSection
          id="preauth"
          icon={<FileTextOutlined />}
          title="Pre-authorization"
          subtitle="Requests and outcomes"
          collapsed={collapsedSections.preauth}
          onToggle={toggleSection}
          action={
            <Button type="link" size="small" onClick={goPreauth}>
              View all
            </Button>
          }
        >
          <MetricGrid
            loading={loading}
            metrics={[
              {
                key: "pa-pending-live",
                title: "Pending PA",
                value: counters?.pa?.pending ?? summary?.pending_pas,
                icon: <CalendarOutlined />,
                color: BRAND.orange,
                onClick: goPreauth,
                subtitle: "Awaiting review",
              },
              {
                key: "pa-queried-live",
                title: "Queried PA",
                value: counters?.pa?.queried ?? summary?.queried_pas,
                icon: <WarningOutlined />,
                color: BRAND.red,
                onClick: goPreauth,
                subtitle: "Needs response",
              },
              {
                key: "pa-approved",
                title: "Approved",
                value: summary?.approved_pas,
                icon: <DollarOutlined />,
                color: BRAND.green,
                onClick: goPreauth,
                subtitle: "Successfully approved",
              },
              {
                key: "pa-rejected",
                title: "Rejected",
                value: summary?.rejected_pas,
                icon: <FileTextOutlined />,
                color: BRAND.red,
                onClick: goPreauth,
                subtitle: "Declined requests",
              },
            ]}
          />
        </DashboardSection>
      ) : null}

      <DashboardSection
        id="encounters"
        icon={<UserOutlined />}
        title="Encounters"
        subtitle="Patient activity by type"
        collapsed={collapsedSections.encounters}
        onToggle={toggleSection}
        action={
          <Button type="link" size="small" onClick={() => actions.showCheckedClients(null)}>
            View all
          </Button>
        }
      >
        <MetricGrid
          loading={loading}
          metrics={[
            {
              key: "enc-total",
              title: "Total encounters",
              value: summary?.totalEncounters,
              icon: <UserOutlined />,
              color: BRAND.blue,
              onClick: () => actions.showCheckedClients(null),
              subtitle: "All encounters",
            },
            {
              key: "enc-active",
              title: "Active",
              value: summary?.activeEncounters,
              icon: <CalendarOutlined />,
              color: BRAND.green,
              onClick: () => actions.showCheckedClients("active"),
              subtitle: "Currently active",
            },
            {
              key: "enc-emergency",
              title: "Emergency",
              value: summary?.emergencyCount,
              icon: <UserOutlined />,
              color: BRAND.red,
              onClick: () => actions.showCheckedClients("emergency"),
              subtitle: "Emergency visits",
            },
          ]}
        />
        {/* Every check-in type (data-driven), creation channel, and benefit items from mapping —
            visible to everyone who can see the dashboard (finding #1). */}
        <EncountersBreakdown
          from={filters?.start || filters?.startDate || filters?.from}
          to={filters?.end || filters?.endDate || filters?.to}
          onTypeClick={(name) => actions.showCheckedClients(String(name).toLowerCase())}
        />
      </DashboardSection>

      <DashboardSection
        id="network"
        icon={<TeamOutlined />}
        title="Policies & network"
        subtitle="Organizations, plans, and membership"
        collapsed={collapsedSections.network}
        onToggle={toggleSection}
      >
        <MetricGrid
          loading={loading}
          metrics={[
            {
              key: "net-orgs",
              title: "Organizations",
              value: summary?.organizations,
              icon: <TeamOutlined />,
              color: BRAND.blue,
              subtitle: "Total organizations",
            },
            {
              key: "net-plans",
              title: "Insurance plans",
              value: summary?.plans,
              icon: <FileTextOutlined />,
              color: BRAND.purple,
              subtitle: "Active plans",
            },
            {
              key: "net-categories",
              title: "Categories",
              value: summary?.categories,
              icon: <TeamOutlined />,
              color: BRAND.blue,
              subtitle: "Plan categories",
            },
            {
              key: "net-enrollees",
              title: "Enrollees",
              value: summary?.enrolees,
              icon: <UserOutlined />,
              color: BRAND.green,
              subtitle: "Registered members",
            },
            {
              key: "net-policies",
              title: "Total policies",
              value: summary?.totalPolicies,
              icon: <FileTextOutlined />,
              color: BRAND.green,
              subtitle: "Policies issued",
            },
            {
              key: "net-corporate",
              title: "Corporate policies",
              value: summary?.corporatePolicies,
              icon: <TeamOutlined />,
              color: BRAND.blue,
              subtitle: "Business policies",
            },
            {
              key: "net-selfpaid",
              title: "Self-paid policies",
              value: summary?.selfPaidPolicies,
              icon: <UserOutlined />,
              color: BRAND.orange,
              subtitle: "Individual policies",
            },
            {
              key: "net-cost-paid",
              title: "Total cost paid",
              value: summary?.total_cost_paid,
              prefix: "₦",
              icon: <DollarOutlined />,
              color: BRAND.green,
              subtitle: "Amount disbursed",
            },
            {
              key: "net-pas",
              title: "Total PAs",
              value: summary?.total_pas,
              icon: <FileTextOutlined />,
              color: BRAND.blue,
              subtitle: "Pre-authorizations",
            },
            {
              key: "net-items",
              title: "Items",
              value: summary?.items,
              icon: <FileTextOutlined />,
              color: BRAND.blue,
              subtitle: "Catalog items",
            },
            {
              key: "net-facilities-total",
              title: "Facilities",
              value: summary?.facilitiesCount,
              icon: <BankOutlined />,
              color: BRAND.orange,
              subtitle: "Registered facilities",
            },
            ...(isAdmin
              ? [
                  {
                    key: "net-enrollees-new",
                    title: "New enrollees",
                    value: counters?.enrollees?.new,
                    icon: <UserOutlined />,
                    color: BRAND.green,
                    onClick: () => actions.navigate("/search?filter=new"),
                    subtitle: "Recently added",
                  },
                  {
                    key: "net-enrollees-updated",
                    title: "Updated enrollees",
                    value: counters?.enrollees?.updated,
                    icon: <SyncOutlined />,
                    color: BRAND.blue,
                    onClick: () => actions.navigate("/search?filter=updated"),
                    subtitle: "Recently modified",
                  },
                ]
              : []),
          ]}
        />
      </DashboardSection>

      <DashboardSection
        id="facilities"
        icon={<BankOutlined />}
        title="Facilities by region"
        subtitle="Geographic distribution"
        collapsed={collapsedSections.facilities}
        onToggle={toggleSection}
      >
        <MetricGrid
          loading={loading}
          metrics={[
            {
              key: "fac-nc",
              title: "North Central",
              value: summary?.facilities_in_north_central,
              icon: <BankOutlined />,
              color: BRAND.green,
            },
            {
              key: "fac-ne",
              title: "North East",
              value: summary?.facilities_in_north_east,
              icon: <BankOutlined />,
              color: BRAND.orange,
            },
            {
              key: "fac-nw",
              title: "North West",
              value: summary?.facilities_in_north_west,
              icon: <BankOutlined />,
              color: BRAND.purple,
            },
            {
              key: "fac-se",
              title: "South East",
              value: summary?.facilities_in_south_east,
              icon: <BankOutlined />,
              color: BRAND.blue,
            },
            {
              key: "fac-ss",
              title: "South South",
              value: summary?.facilities_in_south_south,
              icon: <BankOutlined />,
              color: BRAND.green,
            },
            {
              key: "fac-sw",
              title: "South West",
              value: summary?.facilities_in_south_west,
              icon: <BankOutlined />,
              color: BRAND.orange,
            },
          ]}
        />
      </DashboardSection>
    </div>
  );
};

export default DashboardBody;
